import { useEffect, useState } from "react";
import { Calendar, MapPin, Award, ArrowUpRight, Hotel } from "lucide-react";
import { AccommodationDialog } from "@/components/AccommodationDialogs";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { cedi, regClosed, fmtDeadline } from "@/lib/api";
import { offeredCategories } from "@/lib/categories";
import { Countdown } from "@/components/Countdown";
import { EventSponsors } from "@/components/sponsors/EventSponsors";

const Backdrop = ({ e }) => {
  const imgs = [e.image, ...(e.banners || []).filter((b) => b !== e.image)];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (imgs.length < 2) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % imgs.length), 3500);
    return () => clearInterval(t);
  }, [imgs.length]);
  return imgs.map((src, k) => (
    <img key={src} src={src} alt={e.title} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${k === i ? "opacity-50" : "opacity-0"}`} data-testid={`slide-banner-${e.id}-${k}`} />
  ));
};

const Slide = ({ e, onRegister, onAccommodation }) => (
  <div className="relative h-full min-h-[460px] sm:min-h-[520px] rounded-lg overflow-hidden bg-[#111] text-white flex items-end" data-testid={`slide-${e.id}`}>
    <Backdrop e={e} />
    <div className="absolute inset-0 bg-gradient-to-r from-[#111] via-[#111]/75 to-transparent" />
    <div className="relative p-6 pt-10 sm:p-12 sm:pb-14 max-w-3xl min-w-0 w-full">
      <span className="inline-block bg-[#E60000] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] rounded-sm" data-testid={`slide-status-${e.id}`}>{regClosed(e) ? "Registration Closed" : e.status}</span>
      <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight mt-4 leading-[1.05]" data-testid={`slide-title-${e.id}`}>{e.title}</h2>
      {e.theme && <p className="text-white/70 mt-3 text-sm sm:text-base">{e.theme}</p>}
      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 text-sm text-white/80">
        <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-[#E60000]" />{e.date}</span>
        <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#E60000]" />{e.venue}</span>
        {e.cpd_points > 0 && <span className="flex items-center gap-1.5"><Award className="w-4 h-4 text-[#E60000]" />{e.cpd_points} CPD points</span>}
      </div>
      <Countdown start={e.start_date} testId={`slide-countdown-${e.id}`} />
      {e.registration_deadline && !regClosed(e) && <p className="mt-3 text-sm font-semibold text-white" data-testid={`slide-deadline-${e.id}`}>Registration closes {fmtDeadline(e)}</p>}
      <div className="flex flex-wrap items-center gap-4 mt-7">
        {regClosed(e) ? <span className="inline-flex items-center h-11 px-5 rounded-md bg-white/15 text-sm font-semibold" data-testid={`slide-closed-${e.id}`}>Online registration closed</span> : (
        <button onClick={() => onRegister(e)} className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-[#E60000] hover:bg-[#CC0000] text-sm font-semibold transition-colors" data-testid={`slide-register-${e.id}`}>
          Register now <ArrowUpRight className="w-4 h-4" />
        </button>)}
        <button type="button" onClick={() => onAccommodation(e)} className="inline-flex items-center gap-2 min-h-11 px-4 py-2 rounded-md bg-white text-[#111] border border-white hover:bg-slate-100 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" data-testid={`slide-accommodation-${e.id}`}><Hotel className="w-4 h-4 shrink-0" />View accommodation</button>
        <span className="text-xs text-white/80">{offeredCategories(e).map((c) => `${c.name} ${cedi(c.fee)}`).join(" · ")}</span>
      </div>
      <EventSponsors sponsors={e.sponsors} variant="dark" testId={`slide-sponsors-${e.id}`} />
    </div>
  </div>
);

export const EventSlider = ({ events, onRegister }) => {
  const [api, setApi] = useState(null);
  const [index, setIndex] = useState(0);
  const [accommodationEvent, setAccommodationEvent] = useState(null);
  useEffect(() => {
    if (!api) return;
    const onSelect = () => setIndex(api.selectedScrollSnap());
    api.on("select", onSelect);
    const t = accommodationEvent ? null : setInterval(() => api.scrollNext(), 15000);
    return () => { clearInterval(t); api.off("select", onSelect); };
  }, [api, accommodationEvent]);
  if (!events.length) return <div className="h-80 rounded-lg bg-[#111] text-white/60 grid place-items-center" data-testid="slider-empty">No upcoming events yet.</div>;
  return (
    <div className="relative" data-testid="event-slider">
      <Carousel setApi={setApi} opts={{ loop: true }}>
        <CarouselContent>
          {events.map((e, i) => <CarouselItem key={e.id} inert={i === index ? undefined : ""}><Slide e={e} onRegister={onRegister} onAccommodation={setAccommodationEvent} /></CarouselItem>)}
        </CarouselContent>
      </Carousel>
      <AccommodationDialog event={accommodationEvent} onClose={() => setAccommodationEvent(null)} />
      <div className="absolute bottom-5 right-6 flex gap-2">
        {events.map((e, i) => (
          <button key={e.id} onClick={() => api?.scrollTo(i)} aria-label={`Slide ${i + 1}`} data-testid={`slider-dot-${i}`}
            className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${i === index ? "w-8 bg-[#E60000]" : "w-3 bg-white/40 hover:bg-white/70"}`} />
        ))}
      </div>
    </div>
  );
};
